import React, { memo, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Icons from "@fortawesome/free-solid-svg-icons";
import { ReactComponent as PenUnderline } from "../../../../images/pen_underline.svg";
import Tag from "../../../../ui-components/molecules/tag/tag";
import { TagColor } from "../../../../app/data/common/tagColor";
import Button, { ButtonThemes } from "../../../../ui-components/button/button";
import {
  AccessorialApprovalStatus,
  AccessorialDetailKind,
  BillingAccessorialApprovalRequest,
  OperationsAccessorialApprovalRequest,
  ShipmentWorkItemAccessorial
} from "../../../../app/data/shipment-reconciliation/models";
import OperationsReviewDetentionAccessorialDetails, {
  isDetentionDetails
} from "../../operations-review/details/OperationsReviewDetentionAccessorialDetails";
import {
  operationsAccessorialApprovalSelector,
  submitOperationsAccessorialApproval
} from "../../../../slices/shipment-reconciliation/operations-review/operationAccessorialsApprovalSlice";
import {
  billingAccessorialApprovalSelector,
  submitBillingAccessorialApproval
} from "../../../../slices/shipment-reconciliation/billing-review/billingAccessorialsApprovalSlice";
import Loading from "../../../../ui-components/loading/loading";
import "./AccessorialsReviewSection.scss";

const getFormattedCapatlizedString = (rawString: string) => {
  if (!rawString) return rawString;
  return rawString
    .split(/[ ,|_]+/)
    .map((ele) => `${ele.slice(0, 1).toUpperCase()}${ele.slice(1).toLocaleLowerCase()}`)
    .join(" ");
};

const operationStatusMeta = (status: AccessorialApprovalStatus): { label: string; color: TagColor } => {
  switch (status) {
    case AccessorialApprovalStatus.PENDING:
      return { label: "Pending", color: TagColor.RED };
    case AccessorialApprovalStatus.ACCEPTED:
      return { label: "Accepted", color: TagColor.GREEN };
    case AccessorialApprovalStatus.RJECTED:
      return { label: "Rejected", color: TagColor.RED };
    case AccessorialApprovalStatus.NOT_REQUIRED:
      return { label: "Not Required", color: TagColor.GREY };
    default:
      return { label: String(status), color: TagColor.GREY };
  }
};

const isBillingReviewDecided = (a: ShipmentWorkItemAccessorial): boolean =>
  a.billingApprovalStatus === AccessorialApprovalStatus.ACCEPTED ||
  a.billingApprovalStatus === AccessorialApprovalStatus.RJECTED;

const BILLING_LOCK_TOOLTIP = "Billing review is complete; operations decision cannot be edited.";

const formatAccessorialAuditDateTime = (value: string | null | undefined) =>
  value ? moment(value).format("MM/DD/YY · HH:mm") : "—";

/** US-only: street number + line, city, 2-letter state, optional ZIP. Skips P.O. box style matches. */
const US_ADDRESS_IN_FREE_TEXT =
  /\b(\d{1,5}\s+(?:[A-Za-z0-9#.-]+(?:\s+[A-Za-z0-9#.-]+){0,14}),\s*([^,\n]+),\s*([A-Z]{2})(?:\s+(\d{5}(?:-\d{4})?))?)\b/g;

type UsAddressRange = { start: number; end: number; query: string };

const collectUsAddressRanges = (fragment: string): UsAddressRange[] => {
  const raw: UsAddressRange[] = [];
  for (const m of fragment.matchAll(US_ADDRESS_IN_FREE_TEXT)) {
    const full = m[0];
    if (/P\.?\s*O\.?\s*BOX\b/i.test(full)) continue;
    const start = m.index ?? 0;
    raw.push({ start, end: start + full.length, query: full.replace(/\s+/g, " ").trim() });
  }
  raw.sort((a, b) => a.start - b.start);
  const merged: UsAddressRange[] = [];
  for (const r of raw) {
    const prev = merged[merged.length - 1];
    if (prev && r.start < prev.end) continue;
    merged.push(r);
  }
  return merged;
};

const renderTextWithUsAddressLinks = (fragment: string, keyPrefix: string): React.ReactNode => {
  const ranges = collectUsAddressRanges(fragment);
  if (ranges.length === 0) return fragment;
  const nodes: React.ReactNode[] = [];
  let cursor = 0;
  ranges.forEach((r, j) => {
    if (r.start > cursor) nodes.push(fragment.slice(cursor, r.start));
    const label = fragment.slice(r.start, r.end);
    const href = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(r.query)}`;
    nodes.push(
      <a
        key={`${keyPrefix}-map-${r.start}-${j}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="xgs-or-accessorials-section__application-reason-maps-link"
      >
        {label}
      </a>
    );
    cursor = r.end;
  });
  if (cursor < fragment.length) nodes.push(fragment.slice(cursor));
  return <>{nodes}</>;
};

const renderApplicationReason = (text: string | undefined | null): React.ReactNode => {
  if (text == null || text === "") return "—";
  const parts = text.split(/(\d{7,8})/g);
  return parts.map((part, i) => {
    if (/^\d{7,8}$/.test(part)) {
      return (
        <Link
          key={`${i}-${part}`}
          to={`/shipments/${part}`}
          target="_blank"
          rel="noopener noreferrer"
          className="xgs-operations-review-item-details-v2__probill-link"
        >
          {part}
        </Link>
      );
    }
    return (
      <React.Fragment key={i}>
        {renderTextWithUsAddressLinks(part, String(i))}
      </React.Fragment>
    );
  });
};

const AccessorialsReviewAuditHistory: React.FC<{ accessorial: ShipmentWorkItemAccessorial }> = memo(({ accessorial: a }) => (
  <div className="xgs-or-accessorials-section__audit-history">
    <h3 className="xgs-operations-review-item-details-v2__section-title xgs-or-accessorials-section__audit-history-heading">Audit History</h3>
    <div className="xgs-operations-review-item-details-v2__audit">
      {a.operationApprovalStatus !== AccessorialApprovalStatus.PENDING && a.operationApprovedAt && (
        <div className="xgs-operations-review-item-details-v2__audit-item">
          <span className="xgs-operations-review-item-details-v2__audit-dot" />
          <span className="xgs-operations-review-item-details-v2__audit-line" />
          <div className="xgs-operations-review-item-details-v2__audit-title">
            {a.operationApprovalStatus === AccessorialApprovalStatus.ACCEPTED ? "Accepted by Operations" : "Rejected by Operations"}
          </div>
          <div className="xgs-operations-review-item-details-v2__audit-meta">
            {formatAccessorialAuditDateTime(a.operationApprovedAt)}
            {a.operationApprovedBy ? ` · ${a.operationApprovedBy}` : ""}
          </div>
        </div>
      )}
      <div className="xgs-operations-review-item-details-v2__audit-item">
        <span className="xgs-operations-review-item-details-v2__audit-dot" />
        <div className="xgs-operations-review-item-details-v2__audit-title">Audit finding recorded</div>
        <div className="xgs-operations-review-item-details-v2__audit-meta">
          {formatAccessorialAuditDateTime(a.createdAt)}
          {a.createdBy ? ` · ${a.createdBy}` : ""}
        </div>
      </div>
    </div>
  </div>
));

/** Initial expand: single row always; multiple rows → first pending for the active review context. */
const defaultExpandedIdsForAccessorials = (list: ShipmentWorkItemAccessorial[], billingMode: boolean): Record<string, boolean> => {
  if (!list.length) return {};
  if (list.length === 1) {
    return { [list[0].id]: true };
  }
  const firstPending = list.find((a) =>
    billingMode
      ? a.billingApprovalStatus === AccessorialApprovalStatus.PENDING
      : a.operationApprovalStatus === AccessorialApprovalStatus.PENDING
  );
  return firstPending ? { [firstPending.id]: true } : {};
};

export type AccessorialsReviewContext = "operations" | "billing";

export interface AccessorialsReviewSectionProps {
  accessorials: ShipmentWorkItemAccessorial[];
  loading: boolean;
  shipmentWorkItemId: number;
  terminalAirportCode: string;
  terminalName: string;
  terminalNumber: number;
  probillNumber: number;
  shipmentWeight?: number | null;
  onRefetch: () => void;
  reviewContext?: AccessorialsReviewContext;
  onAfterSubmit?: (accessorialId: string, approvalStatus: AccessorialApprovalStatus, accessorialRate: string) => void;
  onAfterBillingSubmit?: (accessorialId: string, approvalStatus: AccessorialApprovalStatus) => void;
}

const AccessorialsReviewSection: React.FC<AccessorialsReviewSectionProps> = memo(
  ({
    accessorials,
    loading,
    shipmentWorkItemId,
    terminalAirportCode,
    terminalName,
    terminalNumber,
    probillNumber,
    shipmentWeight,
    onAfterSubmit,
    onAfterBillingSubmit,
    onRefetch,
    reviewContext = "operations"
  }) => {
    const dispatch = useDispatch();
    const operationsAccessorialApprovalState = useSelector(operationsAccessorialApprovalSelector);
    const billingAccessorialApprovalState = useSelector(billingAccessorialApprovalSelector);

    const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>({});
    const [editingId, setEditingId] = useState<string | null>(null);
    const [draftNotes, setDraftNotes] = useState<Record<string, string>>({});
    const [submittingId, setSubmittingId] = useState<string | null>(null);
    const [billingDraftNotes, setBillingDraftNotes] = useState<Record<string, string>>({});
    const [billingDraftRates, setBillingDraftRates] = useState<Record<string, string>>({});
    const [billingSubmittingId, setBillingSubmittingId] = useState<string | null>(null);
    const [billingEditingId, setBillingEditingId] = useState<string | null>(null);

    useEffect(() => {
      setExpandedIds({});
      setEditingId(null);
      setDraftNotes({});
      setSubmittingId(null);
      setBillingDraftNotes({});
      setBillingDraftRates({});
      setBillingSubmittingId(null);
      setBillingEditingId(null);
    }, [shipmentWorkItemId]);

    const accessorialsExpandSignature = useMemo(
      () =>
        accessorials
          .map((a) => `${a.id}:${a.operationApprovalStatus}:${a.billingApprovalStatus}`)
          .join("|"),
      [accessorials]
    );

    const terminalLine = useMemo(
      () => `${terminalAirportCode} - ${getFormattedCapatlizedString(terminalName)} ${terminalNumber}`,
      [terminalAirportCode, terminalName, terminalNumber]
    );

    const isBilling = reviewContext === "billing";

    useEffect(() => {
      if (loading || !accessorials.length) return;
      if (!accessorials.every((a) => a.shipmentWorkItemId === shipmentWorkItemId)) return;
      setExpandedIds((prev) => {
        if (Object.values(prev).some(Boolean)) return prev;
        return defaultExpandedIdsForAccessorials(accessorials, isBilling);
      });
    }, [loading, accessorialsExpandSignature, isBilling, accessorials, shipmentWorkItemId]);

    const toggleExpanded = (id: string) => {
      setExpandedIds((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const getDraft = (id: string, accessorial: ShipmentWorkItemAccessorial) =>
      draftNotes[id] !== undefined ? draftNotes[id] : accessorial.operationNotes || "";

    const setDraft = (id: string, value: string) => {
      setDraftNotes((prev) => ({ ...prev, [id]: value }));
    };

    const getBillingNotesDraft = (id: string, accessorial: ShipmentWorkItemAccessorial) =>
      billingDraftNotes[id] !== undefined ? billingDraftNotes[id] : accessorial.billingNotes || "";

    const getBillingRateDraft = (id: string, accessorial: ShipmentWorkItemAccessorial) =>
      billingDraftRates[id] !== undefined ? billingDraftRates[id] : accessorial.billingRate || "";

    const beginEdit = (a: ShipmentWorkItemAccessorial) => {
      setEditingId(a.id);
      setDraftNotes((prev) => ({ ...prev, [a.id]: a.operationNotes || "" }));
    };

    const cancelEdit = () => {
      setEditingId(null);
    };

    const beginBillingEdit = (a: ShipmentWorkItemAccessorial) => {
      setBillingEditingId(a.id);
      setBillingDraftNotes((prev) => ({ ...prev, [a.id]: a.billingNotes || "" }));
      setBillingDraftRates((prev) => ({ ...prev, [a.id]: a.billingRate != null && a.billingRate !== "" ? String(a.billingRate) : "" }));
    };

    const cancelBillingEdit = (id: string) => {
      setBillingEditingId((prev) => (prev === id ? null : prev));
      setBillingDraftNotes((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setBillingDraftRates((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    };

    const submitAccessorial = (accessorial: ShipmentWorkItemAccessorial, approvalStatus: AccessorialApprovalStatus) => {
      const notes = draftNotes[accessorial.id] !== undefined ? draftNotes[accessorial.id] : accessorial.operationNotes || "";
      if (approvalStatus === AccessorialApprovalStatus.RJECTED && !notes.trim()) {
        toast.error("Please provide a note when rejecting an accessorial.");
        return;
      }
      const payload: OperationsAccessorialApprovalRequest = {
        approvalStatus,
        notes: notes.trim() || undefined
      };
      setSubmittingId(accessorial.id);
      dispatch(
        submitOperationsAccessorialApproval(
          shipmentWorkItemId,
          accessorial.id,
          payload,
          () => {
            onAfterSubmit?.(accessorial.id, approvalStatus, accessorial.accessorialDefinition.rate);
            toast.info("Accessorial approval status stored successfully.");
            setEditingId(null);
            setSubmittingId(null);
            setDraftNotes((prev) => {
              const next = { ...prev };
              delete next[accessorial.id];
              return next;
            });
            onRefetch();
          },
          (error) => {
            setSubmittingId(null);
            toast.error(error);
          }
        )
      );
    };

    const submitBillingAccessorial = (accessorial: ShipmentWorkItemAccessorial, approvalStatus: AccessorialApprovalStatus) => {
      const notes = getBillingNotesDraft(accessorial.id, accessorial);
      const rateStr = getBillingRateDraft(accessorial.id, accessorial).trim();
      if (approvalStatus === AccessorialApprovalStatus.RJECTED && !notes.trim()) {
        toast.error("Please provide a rejection reason while rejecting an accessorial.");
        return;
      }
      if (approvalStatus === AccessorialApprovalStatus.ACCEPTED) {
        const rateNum = Number(rateStr);
        if (!rateStr || Number.isNaN(rateNum)) {
          toast.error("Please provide the amount for accessorial charge added on the invoice.");
          return;
        }
      }
      const payload: BillingAccessorialApprovalRequest = {
        approvalStatus,
        notes: notes.trim() || undefined,
        ...(approvalStatus === AccessorialApprovalStatus.ACCEPTED ? { billingRate: Number(rateStr) } : {})
      };
      setBillingSubmittingId(accessorial.id);
      dispatch(
        submitBillingAccessorialApproval(
          shipmentWorkItemId,
          accessorial.id,
          payload,
          () => {
            onAfterBillingSubmit?.(accessorial.id, approvalStatus);
            toast.info("Accessorial approval status stored successfully.");
            setBillingSubmittingId(null);
            setBillingEditingId((prev) => (prev === accessorial.id ? null : prev));
            setBillingDraftNotes((prev) => {
              const next = { ...prev };
              delete next[accessorial.id];
              return next;
            });
            setBillingDraftRates((prev) => {
              const next = { ...prev };
              delete next[accessorial.id];
              return next;
            });
            onRefetch();
          },
          (error) => {
            setBillingSubmittingId(null);
            toast.error(error);
          }
        )
      );
    };

    const showAccessorialsBlockingLoader = loading && (!accessorials || accessorials.length === 0);

    if (showAccessorialsBlockingLoader) {
      return (
        <div className="xgs-or-accessorials-section xgs-or-accessorials-section--loading">
          <h3 className="xgs-or-accessorials-section__title">Accessorials</h3>
          <div className="xgs-or-accessorials-section__loading-row" role="status" aria-live="polite">
            <Loading isLoading />
            <span className="xgs-or-accessorials-section__loading-text">Loading accessorials…</span>
          </div>
        </div>
      );
    }

    if (!accessorials || accessorials.length === 0) {
      return <div className="xgs-or-accessorials-section xgs-or-accessorials-section--empty">No Accessorials available</div>;
    }

    return (
      <div className="xgs-or-accessorials-section">
        <h3 className="xgs-or-accessorials-section__title">Accessorials</h3>
        {accessorials.map((a) => {
          const expanded = !!expandedIds[a.id];
          const headerMeta = isBilling ? operationStatusMeta(a.billingApprovalStatus) : operationStatusMeta(a.operationApprovalStatus);
          const lockedByBilling = isBillingReviewDecided(a);
          const isEditing = editingId === a.id;
          const isPending = a.operationApprovalStatus === AccessorialApprovalStatus.PENDING;
          const isDecided =
            a.operationApprovalStatus === AccessorialApprovalStatus.ACCEPTED ||
            a.operationApprovalStatus === AccessorialApprovalStatus.RJECTED;
          const submitting = submittingId === a.id && operationsAccessorialApprovalState.loading;
          const billingSubmitting = billingSubmittingId === a.id && billingAccessorialApprovalState.loading;

          const isBillingPending = a.billingApprovalStatus === AccessorialApprovalStatus.PENDING;
          const isBillingDecided =
            a.billingApprovalStatus === AccessorialApprovalStatus.ACCEPTED ||
            a.billingApprovalStatus === AccessorialApprovalStatus.RJECTED;
          const billingEditingThis = billingEditingId === a.id;
          const showBillingDecisionForm = isBilling && (isBillingPending || (isBillingDecided && billingEditingThis));
          const showBillingDecidedReadonly = isBilling && isBillingDecided && !billingEditingThis;
          const billingFormIsDecidedEdit = isBilling && isBillingDecided && billingEditingThis;

          const opsBannerTitle =
            a.operationApprovalStatus === AccessorialApprovalStatus.ACCEPTED
              ? "Accepted by Operations"
              : a.operationApprovalStatus === AccessorialApprovalStatus.RJECTED
                ? "Rejected by Operations"
                : "Operations Review";

          return (
            <div key={a.id} className="xgs-or-accessorials-section__item">
              <button type="button" className="xgs-or-accessorials-section__header" onClick={() => toggleExpanded(a.id)} aria-expanded={expanded}>
                <span className="xgs-or-accessorials-section__name">{getFormattedCapatlizedString(a.accessorialDefinition?.name || "")}</span>
                <Tag mods={{ color: headerMeta.color }}>{headerMeta.label}</Tag>
              </button>
              {expanded && (
                <div className="xgs-or-accessorials-section__body">
                  <h3 className="xgs-operations-review-item-details-v2__section-title xgs-or-accessorials-section__audit-finding-heading">
                    Audit Finding
                  </h3>
                  <div className="xgs-operations-review-item-details-v2__panel xgs-or-accessorials-section__audit-finding-panel">
                    <div className="xgs-or-accessorials-section__audit-finding-reason">
                      <div className="xgs-operations-review-item-details-v2__muted">Application reason</div>
                      <div className="xgs-or-accessorials-section__audit-finding-reason-text">{renderApplicationReason(a.applicationReason)}</div>
                    </div>

                    {isBilling && a.operationApprovalStatus === AccessorialApprovalStatus.NOT_REQUIRED && (
                      <div className="xgs-or-accessorials-section__meta xgs-or-accessorials-section__meta--spaced">
                        No operations approval required for this accessorial.
                      </div>
                    )}
                    {isBilling && isPending && (
                      <div className="xgs-or-accessorials-section__meta xgs-or-accessorials-section__meta--spaced">
                        Operations review is pending for this accessorial.
                      </div>
                    )}
                    {isBilling && isDecided && (
                      <div
                        className={`xgs-or-accessorials-section__review-banner ${
                          a.operationApprovalStatus === AccessorialApprovalStatus.ACCEPTED
                            ? "xgs-or-accessorials-section__review-banner--accepted"
                            : "xgs-or-accessorials-section__review-banner--rejected"
                        }`}
                      >
                        <div
                          className={`xgs-or-accessorials-section__review-banner-icon ${
                            a.operationApprovalStatus === AccessorialApprovalStatus.ACCEPTED
                              ? "xgs-or-accessorials-section__review-banner-icon--accepted"
                              : "xgs-or-accessorials-section__review-banner-icon--rejected"
                          }`}
                          aria-hidden
                        >
                          <FontAwesomeIcon
                            icon={a.operationApprovalStatus === AccessorialApprovalStatus.ACCEPTED ? Icons.faCheck : Icons.faTimes}
                            size="sm"
                          />
                        </div>
                        <div className="xgs-or-accessorials-section__review-banner-content">
                          <div className="xgs-or-accessorials-section__review-banner-title">{opsBannerTitle}</div>
                          <div className="xgs-or-accessorials-section__review-banner-line2">
                            <strong className="xgs-or-accessorials-section__review-banner-by">{a.operationApprovedBy || "—"}</strong>
                            <span className="xgs-or-accessorials-section__review-banner-sep" aria-hidden>
                              {" "}
                              •{" "}
                            </span>
                            <span>{terminalLine}</span>
                            <span className="xgs-or-accessorials-section__review-banner-sep" aria-hidden>
                              {" "}
                              •{" "}
                            </span>
                            <span className="xgs-or-accessorials-section__review-banner-date">
                              {formatAccessorialAuditDateTime(a.operationApprovedAt)}
                            </span>
                          </div>
                          <div className="xgs-or-accessorials-section__review-banner-notes">
                            <strong>Review Notes : </strong>
                            <span className="xgs-or-accessorials-section__review-banner-notes-text">{a.operationNotes || "—"}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {!isBilling && isPending && (
                      <>
                        <textarea
                          className="xgs-or-accessorials-section__textarea"
                          placeholder="Notes (required for reject)"
                          value={getDraft(a.id, a)}
                          onChange={(e) => setDraft(a.id, e.currentTarget.value)}
                          maxLength={500}
                          disabled={submitting}
                        />
                        <div className="xgs-or-accessorials-section__actions">
                          <Button
                            theme={ButtonThemes.blue}
                            type="button"
                            disabled={submitting}
                            spinner={submitting}
                            onClick={() => submitAccessorial(a, AccessorialApprovalStatus.ACCEPTED)}
                          >
                            Accept
                          </Button>
                          <Button
                            theme={ButtonThemes.red}
                            type="button"
                            disabled={submitting}
                            spinner={submitting}
                            onClick={() => submitAccessorial(a, AccessorialApprovalStatus.RJECTED)}
                          >
                            Reject
                          </Button>
                        </div>
                      </>
                    )}
                    {!isBilling && isDecided && !isEditing && (
                      <div
                        className={`xgs-or-accessorials-section__review-banner ${
                          a.operationApprovalStatus === AccessorialApprovalStatus.ACCEPTED
                            ? "xgs-or-accessorials-section__review-banner--accepted"
                            : "xgs-or-accessorials-section__review-banner--rejected"
                        }`}
                      >
                        <div
                          className={`xgs-or-accessorials-section__review-banner-icon ${
                            a.operationApprovalStatus === AccessorialApprovalStatus.ACCEPTED
                              ? "xgs-or-accessorials-section__review-banner-icon--accepted"
                              : "xgs-or-accessorials-section__review-banner-icon--rejected"
                          }`}
                          aria-hidden
                        >
                          <FontAwesomeIcon
                            icon={a.operationApprovalStatus === AccessorialApprovalStatus.ACCEPTED ? Icons.faCheck : Icons.faTimes}
                            size="sm"
                          />
                        </div>
                        <div className="xgs-or-accessorials-section__review-banner-content">
                          <div className="xgs-or-accessorials-section__review-banner-title">{opsBannerTitle}</div>
                          <div className="xgs-or-accessorials-section__review-banner-line2">
                            <strong className="xgs-or-accessorials-section__review-banner-by">{a.operationApprovedBy || "—"}</strong>
                            <span className="xgs-or-accessorials-section__review-banner-sep" aria-hidden>
                              {" "}
                              •{" "}
                            </span>
                            <span>{terminalLine}</span>
                            <span className="xgs-or-accessorials-section__review-banner-sep" aria-hidden>
                              {" "}
                              •{" "}
                            </span>
                            <span className="xgs-or-accessorials-section__review-banner-date">
                              {formatAccessorialAuditDateTime(a.operationApprovedAt)}
                            </span>
                          </div>
                          <div className="xgs-or-accessorials-section__review-banner-notes">
                            <strong>Review Notes : </strong>
                            <span className="xgs-or-accessorials-section__review-banner-notes-text">{a.operationNotes || "—"}</span>
                          </div>
                        </div>
                        <span
                          className={`xgs-or-accessorials-section__edit-icon-host ${lockedByBilling ? "xgs-or-accessorials-section__edit-icon-host--locked" : ""}`}
                          title={lockedByBilling ? BILLING_LOCK_TOOLTIP : "Edit"}
                        >
                          <button
                            type="button"
                            className="xgs-or-accessorials-section__edit-icon-btn"
                            disabled={lockedByBilling}
                            onClick={() => beginEdit(a)}
                            aria-label="Edit operations review"
                          >
                            <PenUnderline className="xgs-or-accessorials-section__edit-icon-svg" aria-hidden />
                          </button>
                        </span>
                      </div>
                    )}
                    {!isBilling && isDecided && isEditing && (
                      <>
                        <textarea
                          className="xgs-or-accessorials-section__textarea"
                          placeholder="Notes (required for reject)"
                          value={getDraft(a.id, a)}
                          onChange={(e) => setDraft(a.id, e.currentTarget.value)}
                          maxLength={500}
                          disabled={submitting}
                        />
                        <div className="xgs-or-accessorials-section__actions">
                          <Button
                            theme={ButtonThemes.blue}
                            type="button"
                            disabled={submitting}
                            spinner={submitting}
                            onClick={() => submitAccessorial(a, AccessorialApprovalStatus.ACCEPTED)}
                          >
                            Accept
                          </Button>
                          <Button
                            theme={ButtonThemes.red}
                            type="button"
                            disabled={submitting}
                            spinner={submitting}
                            onClick={() => submitAccessorial(a, AccessorialApprovalStatus.RJECTED)}
                          >
                            Reject
                          </Button>
                          <Button theme={ButtonThemes.gray} type="button" disabled={submitting} onClick={cancelEdit}>
                            Cancel
                          </Button>
                        </div>
                      </>
                    )}
                    {!isBilling && a.operationApprovalStatus === AccessorialApprovalStatus.NOT_REQUIRED && (
                      <div className="xgs-or-accessorials-section__meta">No operations approval required for this accessorial.</div>
                    )}
                  </div>

                  {isBilling && (
                    <>
                      <h3 className="xgs-operations-review-item-details-v2__section-title xgs-or-accessorials-section__billing-heading">
                        Billing Review
                      </h3>
                      <div className="xgs-operations-review-item-details-v2__panel xgs-or-accessorials-section__billing-panel">
                        {showBillingDecisionForm && (
                          <>
                            <div className="xgs-operations-review-item-details-v2__muted">Amount added on invoice for the accessorial</div>
                            <input
                              type="text"
                              className="xgs-or-accessorials-section__billing-rate-input"
                              placeholder="Accessorial charge"
                              value={getBillingRateDraft(a.id, a)}
                              onChange={(e) => {
                                const { value } = e.currentTarget;
                                setBillingDraftRates((prev) => ({ ...prev, [a.id]: value }));
                              }}
                              disabled={billingSubmitting}
                            />
                            <textarea
                              className="xgs-or-accessorials-section__textarea"
                              placeholder="Notes (required for reject)"
                              value={getBillingNotesDraft(a.id, a)}
                              onChange={(e) => {
                                const { value } = e.currentTarget;
                                setBillingDraftNotes((prev) => ({ ...prev, [a.id]: value }));
                              }}
                              maxLength={500}
                              disabled={billingSubmitting}
                            />
                            <div className="xgs-or-accessorials-section__actions">
                              <Button
                                theme={ButtonThemes.blue}
                                type="button"
                                disabled={billingSubmitting}
                                spinner={billingSubmitting}
                                onClick={() => submitBillingAccessorial(a, AccessorialApprovalStatus.ACCEPTED)}
                              >
                                Accept
                              </Button>
                              <Button
                                theme={ButtonThemes.red}
                                type="button"
                                disabled={billingSubmitting}
                                spinner={billingSubmitting}
                                onClick={() => submitBillingAccessorial(a, AccessorialApprovalStatus.RJECTED)}
                              >
                                Reject
                              </Button>
                              {billingFormIsDecidedEdit && (
                                <Button
                                  theme={ButtonThemes.gray}
                                  type="button"
                                  disabled={billingSubmitting}
                                  onClick={() => cancelBillingEdit(a.id)}
                                >
                                  Cancel
                                </Button>
                              )}
                            </div>
                          </>
                        )}
                        {showBillingDecidedReadonly && (
                          <div
                            className={`xgs-or-accessorials-section__review-banner ${
                              a.billingApprovalStatus === AccessorialApprovalStatus.ACCEPTED
                                ? "xgs-or-accessorials-section__review-banner--accepted"
                                : "xgs-or-accessorials-section__review-banner--rejected"
                            }`}
                          >
                            <div
                              className={`xgs-or-accessorials-section__review-banner-icon ${
                                a.billingApprovalStatus === AccessorialApprovalStatus.ACCEPTED
                                  ? "xgs-or-accessorials-section__review-banner-icon--accepted"
                                  : "xgs-or-accessorials-section__review-banner-icon--rejected"
                              }`}
                              aria-hidden
                            >
                              <FontAwesomeIcon
                                icon={a.billingApprovalStatus === AccessorialApprovalStatus.ACCEPTED ? Icons.faCheck : Icons.faTimes}
                                size="sm"
                              />
                            </div>
                            <div className="xgs-or-accessorials-section__review-banner-content">
                              <div className="xgs-or-accessorials-section__review-banner-title">
                                {a.billingApprovalStatus === AccessorialApprovalStatus.ACCEPTED
                                  ? "Accepted by Billing"
                                  : "Rejected by Billing"}
                              </div>
                              <div className="xgs-or-accessorials-section__review-banner-line2">
                                <strong className="xgs-or-accessorials-section__review-banner-by">{a.billingApprovedBy || "—"}</strong>
                                <span className="xgs-or-accessorials-section__review-banner-sep" aria-hidden>
                                  {" "}
                                  •{" "}
                                </span>
                                <span>{terminalLine}</span>
                                <span className="xgs-or-accessorials-section__review-banner-sep" aria-hidden>
                                  {" "}
                                  •{" "}
                                </span>
                                <span className="xgs-or-accessorials-section__review-banner-date">
                                  {formatAccessorialAuditDateTime(a.billingApprovedAt)}
                                </span>
                              </div>
                              <div className="xgs-or-accessorials-section__review-banner-notes">
                                <strong>Billing Notes : </strong>
                                <span className="xgs-or-accessorials-section__review-banner-notes-text">{a.billingNotes || "—"}</span>
                              </div>
                              {a.billingApprovalStatus === AccessorialApprovalStatus.ACCEPTED && a.billingRate != null && (
                                <div className="xgs-or-accessorials-section__review-banner-notes">
                                  <strong>Invoice amount : </strong>
                                  <span>{String(a.billingRate)}</span>
                                </div>
                              )}
                            </div>
                            <span className="xgs-or-accessorials-section__edit-icon-host">
                              <button
                                type="button"
                                className="xgs-or-accessorials-section__edit-icon-btn"
                                onClick={() => beginBillingEdit(a)}
                                aria-label="Edit billing review"
                              >
                                <PenUnderline className="xgs-or-accessorials-section__edit-icon-svg" aria-hidden />
                              </button>
                            </span>
                          </div>
                        )}
                        {a.billingApprovalStatus === AccessorialApprovalStatus.NOT_REQUIRED && (
                          <div className="xgs-or-accessorials-section__meta">No billing approval required for this accessorial.</div>
                        )}
                      </div>
                    </>
                  )}

                  {a.detailKind === AccessorialDetailKind.DETENTION && isDetentionDetails(a.details) && (
                    <OperationsReviewDetentionAccessorialDetails
                      accessorial={a}
                      probillNumber={probillNumber}
                      shipmentWeight={shipmentWeight}
                      terminalNumber={terminalNumber}
                    />
                  )}
                  <AccessorialsReviewAuditHistory accessorial={a} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }
);

export default AccessorialsReviewSection;
